/**
 * Mode A (§5.9): a service already running on the host — a database, a message
 * broker, a cache, anything a container publishes a port for — reachable from a
 * session. The grammar of that: what a container looks like, what a selection
 * looks like, and how a `localhost` URL becomes one a guest can dial.
 *
 * **This is the one deliberate hole in the isolation boundary**, and everything
 * here exists to keep it as small as it can be. The session's network policy is
 * deny-by-default (§7.1); a selection opens **one address and one port** —
 * `allow(d => d.ip(...))`, never the runtime's `host` or `private` groups. The
 * VM boundary does not protect the service behind that rule: an agent that has
 * been steered by repo content (§7.3) can read whatever it holds and can destroy
 * it. That is the cost of the mode, and the reason it is opt-in per workspace
 * and never an application default.
 *
 * Note the two parts are separate on purpose. Mode A is a *policy rule*, not
 * traffic through the egress gate — a Postgres or AMQP connection is not HTTP
 * and never reads `http_proxy` (§7.1), so the gate and its refusal of private
 * address space are untouched by any of this.
 *
 * Nothing here is database-specific. `hostservice-catalog.ts` names the services
 * worth naming, and a service it cannot name is still offered: the label is a
 * convenience, never the thing that decides whether a container is usable.
 */
import {
  CREDENTIAL_ENV,
  CREDENTIALLESS,
  KIND_BY_IMAGE,
  PATHLESS,
  SCHEME_BY_KIND,
  SERVICE_BY_PORT
} from './hostservice-catalog';

/**
 * The repository part of an OCI reference, lowercased: no registry, no
 * namespace, no tag and no digest. `mcr.microsoft.com/azure-storage/azurite:3.29`
 * is `azurite`, and that is what both the kind table and the variable name read.
 */
export function imageRepository(image: string): string {
  const withoutDigest = image.split('@')[0];
  const lastSegment = withoutDigest.split('/').pop() ?? '';
  return lastSegment.split(':')[0].trim().toLowerCase();
}

/** The tag, when the reference carries one that is not `latest`. */
function imageTag(image: string): string {
  const withoutDigest = image.split('@')[0];
  const lastSegment = withoutDigest.split('/').pop() ?? '';
  const tag = lastSegment.includes(':') ? lastSegment.slice(lastSegment.indexOf(':') + 1) : '';
  return tag === 'latest' ? '' : tag.trim().toLowerCase();
}

/**
 * The variable name a selection is prefilled with: the image, uppercased.
 *
 * Not the service's conventional variable (`DATABASE_URL`, `MONGO_URL`), and
 * that is the point. Those names collide — two containers Airlock could not
 * label both prefilled `SERVICE_URL`, so adding the second silently overwrote
 * the first's value in every project. The image is what the user pulled, it is
 * different for every container worth selecting, and it reads back as the thing
 * they picked. A name their framework insists on is one edit away, and the
 * field is right there.
 *
 * `mongo:7.1` → `MONGO_7_1`; `mcr.microsoft.com/azure-storage/azurite` →
 * `AZURITE`.
 */
export function envVarForImage(image: string): string {
  const repository = imageRepository(image);
  const tag = imageTag(image);
  const name = [repository, tag]
    .filter(Boolean)
    .join('_')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
  if (!name) return 'SERVICE_URL';
  // A name opening on a digit is not a variable in any shell; the underscore is
  // what a person would have typed themselves.
  return /^[0-9]/.test(name) ? `_${name}` : name;
}

/**
 * `MONGO_7_1` when nothing holds it yet, `MONGO_7_1_2` when something does.
 * Uniqueness is the whole reason the name comes from the image, so the picker
 * cannot be allowed to hand out the same one twice either.
 */
export function uniqueEnvVar(wanted: string, taken: readonly string[]): string {
  const used = new Set(taken.map((name) => name.trim().toUpperCase()));
  if (!used.has(wanted.toUpperCase())) return wanted;
  for (let n = 2; n < 100; n += 1) {
    const candidate = `${wanted}_${n}`;
    if (!used.has(candidate.toUpperCase())) return candidate;
  }
  return wanted;
}

/** One published port of a host container, as `docker ps` reports it. */
export interface PublishedPort {
  /** The port on the host — what a guest would dial. */
  host: number;
  /** The port inside the container, kept because it names the service better. */
  container: number;
}

/** What a container's own environment says it was started with (§5.9). */
export interface ContainerCredentials {
  user?: string;
  password?: string;
  /** The database, bucket or vhost the URL addresses, where the kind has one. */
  database?: string;
}

/** A running host container a workspace could point a session at (§5.9). */
export interface HostContainer {
  id: string;
  name: string;
  image: string;
  ports: PublishedPort[];
  /** Guessed from the ports — a label, never a promise. */
  kind: string | null;
  /**
   * What the container was started with, read from its own environment.
   *
   * It exists because the alternative was worse. The prefill used to be
   * `postgresql://user:password@localhost:5432/name` — a URL that cannot
   * connect, offered as though it could, with no hint anywhere of what the real
   * values were. Every user's first act was to go and read the credentials out
   * of their own compose file and type them back in here.
   *
   * It is a **prefill and stays editable**, on the same terms as `installCmd`
   * (§15). And note what it means for storage: these values reach
   * `HostService.url`, which lives in the workspace config in plain SQLite like
   * the URL a person would have typed themselves. That is the existing class of
   * this field — a dev instance's connection string — and this does not change
   * it; it is a reason the warning above the list says to point Mode A at a
   * disposable instance with least-privilege credentials.
   */
  credentials?: ContainerCredentials;
}

/**
 * The credentials a container's environment states, for the kind it is.
 *
 * `env` is host output — `docker inspect` on someone else's container — so every
 * entry is checked rather than trusted, and only the names above are read. A
 * value is capped: a connection string is not where a megabyte belongs, and this
 * one ends up in a config file.
 *
 * Neo4j is the one shape that is not a pair of variables: `NEO4J_AUTH` is
 * `user/password` in one string, and `none` when auth is off.
 */
export function credentialsFrom(env: readonly string[], kind: string | null): ContainerCredentials | undefined {
  const spec = kind ? CREDENTIAL_ENV[kind] : undefined;
  if (!spec) return undefined;
  const values = new Map<string, string>();
  for (const entry of env) {
    if (typeof entry !== 'string') continue;
    const at = entry.indexOf('=');
    if (at <= 0) continue;
    values.set(entry.slice(0, at), entry.slice(at + 1, at + 1 + 200));
  }
  const first = (names?: string[]): string | undefined => names?.map((n) => values.get(n)).find((v) => !!v);

  const auth = values.get('NEO4J_AUTH');
  if (kind === 'Neo4j') {
    if (!auth || auth === 'none') return undefined;
    const [user, password] = auth.split('/');
    return user ? { user, password: password || undefined } : undefined;
  }

  const password = first(spec.password);
  const user = first(spec.user) ?? (password ? spec.asUser : undefined);
  const database = first(spec.database) ?? (kind === 'PostgreSQL' ? user : undefined);
  if (!user && !password && !database) return undefined;
  return { user, password, database };
}

/**
 * A host service this workspace's sessions may reach — a database, a broker, a
 * cache, whatever the code dials. Stored on the workspace and nowhere else:
 * "this repository needs that service" is a judgement about the code, exactly as
 * `blockedDomains` is (§4.1), and no application default could be right.
 */
export interface HostService {
  id: string;
  /** What the container was called when it was picked — for the UI only. */
  label: string;
  /** The host port the egress rule opens. The whole of the hole. */
  port: number;
  /**
   * The environment variable each project gets it as. One name, because a
   * connection URL is what every client reads and the app cannot know which of
   * `DATABASE_URL`/`BROKER_URL`/… this project wants.
   */
  envVar: string;
  /**
   * The connection URL as the user has it locally — `localhost` and all. It is
   * rewritten for the guest at injection time (`rewriteForGuest`), so what is
   * stored is the thing they already paste into their own shell.
   */
  url: string;
}

/** Names an environment variable can have; the guest writes it into a `.env`. */
export function isEnvVarName(value: string): boolean {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(value);
}

/**
 * How many ports one published *range* contributes. Docker prints a range as a
 * single entry, so a range has to be expanded or the container is not offered at
 * all; a handful of ports is a real deployment (a replica set, a broker's wire
 * and console), a thousand is not a service anyone selects, and this is a list a
 * person reads.
 */
const MAX_RANGE_PORTS = 16;

/** A `docker ps` port string: `0.0.0.0:5432->5432/tcp, :::5432->5432/tcp`. */
export function parsePorts(value: string): PublishedPort[] {
  const out = new Map<number, PublishedPort>();
  for (const part of value.split(',')) {
    // Only published TCP ports matter: an unpublished port is unreachable from
    // the host, let alone from a guest, and UDP is not a wire anything here
    // dials.
    // A published range — `0.0.0.0:5000-5001->5000-5001/tcp` — is one entry for
    // several ports: it is expanded, because reading it as nothing left the
    // whole container out of the picker with no way to tell why.
    const match = /(?:^|\s)(?:[\d.]+|\[?[\da-f:]+\]?):(\d+)(?:-(\d+))?->(\d+)(?:-(\d+))?\/tcp/i.exec(part);
    if (!match) continue;
    const host = Number(match[1]);
    const container = Number(match[3]);
    if (!port(host) || !port(container)) continue;
    // The two sides of a range are the same length when Docker prints them; a
    // pair that disagrees is read for as far as both sides go.
    const hostSpan = match[2] ? Number(match[2]) - host : 0;
    const containerSpan = match[4] ? Number(match[4]) - container : 0;
    if (hostSpan < 0 || containerSpan < 0) continue;
    const span = Math.min(hostSpan, containerSpan, MAX_RANGE_PORTS - 1);
    for (let i = 0; i <= span; i++) {
      if (!port(host + i) || !port(container + i)) break;
      // Docker lists IPv4 and IPv6 bindings of the same port separately.
      if (!out.has(host + i)) out.set(host + i, { host: host + i, container: container + i });
    }
  }
  return [...out.values()].sort((a, b) => a.host - b.host);
}

function port(value: number): boolean {
  return Number.isInteger(value) && value > 0 && value <= 65_535;
}

/**
 * Which service this is, guessed from the ports it publishes — the container
 * port first, because that is the one the image chose. The image *name* is
 * deliberately not consulted: `my-app-svc` says nothing and a tag can say
 * anything, while a port is what the client actually dials. A container it
 * cannot name is still offered, unlabelled.
 */
export function guessKind(ports: PublishedPort[], image = ''): string | null {
  for (const p of ports) {
    const byContainer = SERVICE_BY_PORT[p.container];
    if (byContainer) return byContainer;
  }
  // The image, before the *host* port: a host port is whatever was free when the
  // container was started, and `-p 38017:38017` on a `mongo` image is a MongoDB
  // however unconventional both numbers are. Reading the ports alone left that
  // container unlabelled and prefilled `tcp://`, which no driver dials (§5.9).
  const byImage = kindFromImage(image);
  if (byImage) return byImage;
  for (const p of ports) {
    const byHost = SERVICE_BY_PORT[p.host];
    if (byHost) return byHost;
  }
  return null;
}

/** The kind the image's repository name states, where it states one. */
export function kindFromImage(image: string): string | null {
  const repository = imageRepository(image);
  if (!repository) return null;
  for (const [pattern, kind] of KIND_BY_IMAGE) if (pattern.test(repository)) return kind;
  return null;
}

/**
 * One row of `docker ps --format '{{json .}}'`, narrowed. Host output, so every
 * field is checked rather than trusted: a row that is not a container is
 * dropped instead of half-read (§7.4).
 */
export function parseContainer(raw: unknown): HostContainer | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const id = str(row.ID);
  const name = str(row.Names);
  if (!id || !name) return null;
  const ports = parsePorts(str(row.Ports));
  // A container with nothing published cannot be reached from a guest, so it is
  // not a candidate — showing it would be offering something that cannot work.
  if (!ports.length) return null;
  const image = str(row.Image) || 'unknown';
  return { id, name, image, ports, kind: guessKind(ports, image) };
}

function str(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** Every container of a `docker ps` run, newest-listed first, capped. */
export function parseContainers(stdout: string, max = 100): HostContainer[] {
  const out: HostContainer[] = [];
  for (const line of stdout.split('\n')) {
    const text = line.trim();
    if (!text.startsWith('{')) continue;
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      continue; // a partial line is not a container
    }
    const container = parseContainer(parsed);
    if (container) out.push(container);
    if (out.length >= max) break;
  }
  return out;
}

/**
 * What to prefill when a container is picked: a variable name taken from the
 * image (`envVarForImage`), and a URL shaped like the one the user already has.
 *
 * A guess, and it is theirs to correct — the same relationship `installCmd` has
 * to detection (§15). Credentials are left as obvious placeholders rather than
 * invented, so an unedited prefill fails at the service with a clear error
 * instead of looking like it should have worked.
 *
 * The scheme comes from the kind, and the kind now reads the image as well as
 * the ports: a `mongo` published on 38017 used to be labelled nothing and
 * prefilled `tcp://localhost:38017`, which every driver refuses.
 */
export function suggestSelection(
  container: HostContainer,
  /** Variables this workspace already gives its sessions — the name dodges them. */
  taken: readonly string[] = [],
  /**
   * A variable one of this workspace's repositories already reads — `DATABASE_URL`,
   * `REDIS_URL` — when the caller found one that fits this kind (§5.9).
   *
   * It is the better name by a distance: the point of the selection is that some
   * code reads it, and a name derived from the image is a name nothing reads. The
   * image-derived one stays as the fallback, because it is unique per container
   * and two services must never collide.
   */
  wanted?: string
): { envVar: string; url: string; port: number } {
  const chosen = container.ports.find((p) => SERVICE_BY_PORT[p.container]) ?? container.ports[0];
  const kind = container.kind ?? kindFromImage(container.image);
  const scheme = (kind && SCHEME_BY_KIND[kind]) ?? 'tcp';
  const envVar = wanted && isEnvVarName(wanted) ? wanted : uniqueEnvVar(envVarForImage(container.image), taken);
  // What the container was actually started with, where it says (§5.9). The
  // placeholders below are the fallback and nothing more: a URL nobody can
  // connect with, offered as though they could, is what this replaced.
  const found = container.credentials;
  const authority = CREDENTIALLESS.has(scheme)
    ? found?.password
      ? // A password with no user is Redis's shape: `redis://:pass@host`.
        `${encodeURIComponent(found.user ?? '')}:${encodeURIComponent(found.password)}@`
      : ''
    : found?.user || found?.password
      ? `${encodeURIComponent(found.user ?? '')}:${encodeURIComponent(found.password ?? '')}@`
      : 'user:password@';
  // Only the schemes addressing a named store get a path: a broker URL with an
  // invented `/database` on the end is a URL that cannot connect.
  const path = PATHLESS.has(scheme) ? '' : `/${found?.database ?? 'name'}`;
  return {
    envVar,
    port: chosen.host,
    url: `${scheme}://${authority}localhost:${chosen.host}${path}`
  };
}

/**
 * Every spelling of "this machine" a connection URL is written with: the whole
 * loopback /8, the unspecified address a server is bound to and then dialled
 * back on, and the IPv6 loopback both short and fully expanded.
 *
 * Exported because a project dialling a *sibling project* asks the same question
 * of the same strings (§5.19), and two definitions of "this machine" would drift.
 */
export function isLoopbackHost(host: string): boolean {
  const bare = host.replace(/^\[|\]$/g, '').toLowerCase();
  if (bare === 'localhost' || bare === '0.0.0.0') return true;
  if (/^127(?:\.\d{1,3}){3}$/.test(bare)) return true;
  // `::1` and `0:0:0:0:0:0:0:1` are one address. Matched by *spelling* rather
  // than by "any run of zeroes and colons ending in 1", which also accepted
  // `0:1` and `0:0:1` — neither of which is loopback, and both of which this
  // then rewrote for the guest as though they were this machine.
  return /^(::1|(?:0:){7}1|(?:0:)+:1)$/.test(bare);
}

/**
 * The URL a guest can actually dial.
 *
 * `localhost` inside the sandbox is the sandbox, so a URL that works on the host
 * points the session at itself. The measured answer (§7.1, the netgate spike) is
 * that a guest reaches the host at its **LAN address** — not loopback, not the
 * link address the runtime hands it — so that is what the host name becomes.
 *
 * Only the host part is touched. Credentials, port, path and query are the
 * user's and are passed through unchanged. Two things make that harder than a
 * substitution: the host is the segment after the **last** `@`, because a
 * username or a password may read exactly like a host name, and there may be
 * **several** hosts — a comma-separated seed list is MongoDB's normal form, and
 * a driver handed one rewritten host and one untouched dials the guest itself.
 */
export function rewriteForGuest(url: string, hostAddress: string): string {
  return url.replace(/^(\w[\w+.-]*:\/\/)([^/?#]*)/, (_all, scheme: string, authority: string) => {
    const at = authority.lastIndexOf('@');
    const userinfo = at === -1 ? '' : authority.slice(0, at + 1);
    const hosts = authority
      .slice(at + 1)
      .split(',')
      .map((entry) => {
        // `[::1]:6379` keeps its brackets together; everything else splits at
        // the one colon a host:port pair can have.
        const parsed = /^(\[[^\]]*\]|[^:]*)(:.*)?$/.exec(entry.trim());
        if (!parsed || !isLoopbackHost(parsed[1])) return entry;
        return `${hostAddress}${parsed[2] ?? ''}`;
      })
      .join(',');
    return scheme + userinfo + hosts;
  });
}

/**
 * A variable one of this workspace's repositories already reads, offered as the
 * name a selection should take (§5.9).
 *
 * The picker used to name the variable after the image — `POSTGRES_17_ALPINE`,
 * unique per container and read by nothing — and then leave a person to work out
 * for themselves which of their own variables it was supposed to be. The whole
 * point of a selection is that some code reads it, and the code has already said
 * what it calls the thing: it is in the repository's own environment (§5.7).
 *
 * A candidate is a variable whose *name* reads as an address — `DATABASE_URL`,
 * `REDIS_URI`, `AMQP_DSN` — or whose value already carries a scheme. `matches`
 * is the stronger claim on top of that: its current value is dialled with the
 * same scheme this kind of service speaks, which makes it almost certainly the
 * one. Both are offered; only the order between them changes.
 */
export interface ServiceVariable {
  name: string;
  /** The repositories that declare it, so a name can be placed. */
  projects: string[];
  /** Its value is already a URL of this service's own scheme. */
  matches: boolean;
}

/** Names that read as an address rather than as a flag or a secret. */
const ADDRESS_NAME = /(?:^|_)(URL|URI|DSN|ENDPOINT|ADDR|ADDRESS|CONNECTION|CONNECTIONSTRING|CONNECTION_STRING)$/i;

export function serviceVariables(
  projects: readonly { name: string; env: readonly { key: string; value: string }[] }[],
  kind: string | null
): ServiceVariable[] {
  const scheme = (kind && SCHEME_BY_KIND[kind]) ?? null;
  const found = new Map<string, ServiceVariable>();
  for (const project of projects) {
    for (const { key, value } of project.env) {
      const name = key.trim();
      const carriesScheme = /^\w[\w+.-]*:\/\//.test(value.trim());
      if (!isEnvVarName(name) || (!ADDRESS_NAME.test(name) && !carriesScheme)) continue;
      const matches = !!scheme && value.trim().toLowerCase().startsWith(`${scheme}://`);
      const seen = found.get(name);
      if (seen) {
        seen.projects.push(project.name);
        seen.matches ||= matches;
      } else {
        found.set(name, { name, projects: [project.name], matches });
      }
    }
  }
  // The likely one first, then alphabetically — a list of names a person is
  // scanning, not a ranking they have to trust.
  return [...found.values()].sort((a, b) => Number(b.matches) - Number(a.matches) || a.name.localeCompare(b.name));
}

/**
 * Whether a stored selection is still usable, and why not when it is not. The
 * message is shown as-is, so it names the field rather than the rule.
 */
export function selectionProblem(service: Pick<HostService, 'envVar' | 'url' | 'port'>): string | null {
  if (!isEnvVarName(service.envVar)) return 'The environment variable name must look like SERVICE_URL.';
  if (!port(service.port)) return 'The port is not a port.';
  if (!/^\w[\w+.-]*:\/\//.test(service.url.trim())) {
    return 'The connection URL needs a scheme, like postgresql:// or amqp://…';
  }
  return null;
}

/**
 * The variables one selection puts into a project (§5.9).
 *
 * Three, not one. The URL is what a client library reads, but a great deal of
 * configuration wants the two halves separately — a Spring `spring.data.mongodb.host`,
 * a compose-style `${HOST}:${PORT}` — and a user with only the URL ends up
 * writing `${MONGO_7_1#tcp://}` in their own configuration to get them back.
 * `_HOST` and `_PORT` are cheap here and are the difference between that and
 * naming the thing they want.
 *
 * The host and port are read from the URL *after* it was rewritten for the
 * guest, so they name the address this session can actually dial.
 */
export function serviceEnvEntries(envVar: string, url: string): { key: string; value: string }[] {
  const out = [{ key: envVar, value: url }];
  const authority = /^\w[\w+.-]*:\/\/([^/?#]*)/.exec(url)?.[1];
  if (!authority) return out;
  const afterUserinfo = authority.slice(authority.lastIndexOf('@') + 1);
  // A seed list names several hosts; the first is the one a `_HOST` can mean.
  const first = afterUserinfo.split(',')[0].trim();
  const parsed = /^(\[[^\]]*\]|[^:]*)(?::(\d+))?$/.exec(first);
  if (!parsed || !parsed[1]) return out;
  out.push({ key: `${envVar}_HOST`, value: parsed[1].replace(/^\[|\]$/g, '') });
  if (parsed[2]) out.push({ key: `${envVar}_PORT`, value: parsed[2] });
  return out;
}
